/** @jsxImportSource @emotion/react */
import {
  CalendarOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'

import SharedViewSwitcher from 'src/components/ts-components/ViewSwitcher'
import type { ViewSwitcherOption } from 'src/components/ts-components/ViewSwitcher'
import type { GameViewMode } from '../hooks/useGameViewMode'
import { gameViewsLabel } from '../utils/i18n'

interface ViewSwitcherProps {
  mode: GameViewMode
  onChange: (mode: GameViewMode) => void
  disabled?: boolean
  /** Details is hidden on small screens — it cannot render honestly there. */
  showDetails?: boolean
  /**
   * `filters` matches the 40px filter-row controls it sits beside (Oldest /
   * Newest, Past / Today / Upcoming). `subTabs` matches the shorter Live /
   * Scheduler Admin control on the Schedule page.
   */
  size?: 'filters' | 'subTabs'
}

/**
 * The game feature's three views, bound to the shared switcher.
 *
 * This owns what is specific to games — which views exist, their icons, and the
 * `common:` i18n keys their labels come from — while the segmented control
 * itself lives in `ts-components` so the registration step and anything after it
 * render the identical widget rather than a lookalike.
 */
const ViewSwitcher = ({
  mode,
  onChange,
  disabled = false,
  showDetails = true,
  size = 'filters',
}: ViewSwitcherProps) => {
  'use memo'

  const options: ViewSwitcherOption<GameViewMode>[] = [
    {
      value: 'list',
      label: gameViewsLabel('gameViewList'),
      icon: <UnorderedListOutlined />,
      'data-testid': 'game-view-list',
    },
    {
      value: 'calendar',
      label: gameViewsLabel('gameViewCalendar'),
      icon: <CalendarOutlined />,
      'data-testid': 'game-view-calendar',
    },
    ...(showDetails
      ? [
          {
            value: 'details' as GameViewMode,
            label: gameViewsLabel('gameViewDetails'),
            icon: <TableOutlined />,
            emphasized: true,
            'data-testid': 'game-view-details',
          },
        ]
      : []),
  ]

  return (
    <SharedViewSwitcher<GameViewMode>
      mode={mode}
      onChange={onChange}
      options={options}
      ariaLabel={gameViewsLabel('gameViewSwitcherLabel')}
      disabled={disabled}
      size={size}
    />
  )
}

export default ViewSwitcher
